import { nanoid } from "nanoid";
import envVariables from "src/lib/envVariables";
import { isPartnerDiagnosticsEnabled } from "src/lib/partnerDiagnostics";
import reportErrorSentry from "src/lib/reportErrorSentry";
import type { DeviceInfo } from "../types";

const WEB_DEVICE_UUID_KEY = "device-uuid";

/**
 * Collects device information for web platform
 * Returns limited device info with fallbacks for native-only properties
 */
export async function collectWebInfo(): Promise<DeviceInfo> {
  const partnerDiagnostics = isPartnerDiagnosticsEnabled()
    ? await collectPartnerDiagnostics()
    : {};
  return {
    // System Information
    platform: "web",
    operatingSystem: "web",
    osVersion: navigator.userAgent,
    appVersion: envVariables.BUILD_VERSION,
    appBuild: "web",
    webViewVersion: estimateWebKitVersion(),
    locale: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,

    // Hardware (unavailable on web)
    manufacturer: "unavailable",
    model: "unavailable",
    deviceId: getWebDeviceId(),
    isVirtual: false,

    // Network
    networkConnected: navigator.onLine,
    networkType: "unknown",

    // Permissions (unavailable on web)
    pushNotificationStatus: "prompt",
    biometricAvailable: false,
    ...partnerDiagnostics
  };
}

async function collectPartnerDiagnostics(): Promise<Partial<DeviceInfo>> {
  return {
    userAgent: navigator.userAgent,
    screenWidth: screen.width,
    screenHeight: screen.height,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
    devicePixelRatio: window.devicePixelRatio,
    prefersReducedMotion: matchesMedia("(prefers-reduced-motion: reduce)"),
    prefersReducedTransparency: matchesMedia(
      "(prefers-reduced-transparency: reduce)"
    ),
    prefersContrast: matchesMedia("(prefers-contrast: more)")
      ? "more"
      : matchesMedia("(prefers-contrast: less)")
        ? "less"
        : "no-preference",
    webAssemblyAvailable: typeof WebAssembly !== "undefined",
    hardwareConcurrency: navigator.hardwareConcurrency,
    ...(await collectStorageInfo())
  };
}

const matchesMedia = (query: string): boolean =>
  typeof window.matchMedia === "function" && window.matchMedia(query).matches;

async function collectStorageInfo(): Promise<
  Pick<DeviceInfo, "storageQuota" | "storageUsage" | "storagePersisted">
> {
  try {
    const { quota, usage } = await navigator.storage.estimate();
    const storagePersisted = await navigator.storage.persisted();
    return { storageQuota: quota, storageUsage: usage, storagePersisted };
  } catch {
    return {};
  }
}

// Ordered newest first. Probes use CSS features first shipped in that Safari release.
const WEBKIT_PROBES: [version: string, property: string, value: string][] = [
  ["27.0", "color", "alpha(from red / 0.5)"],
  ["26.0", "anchor-name", "--a"],
  ["18.4", "clip-path", "shape(from 0 0, line to 10px 0)"],
  ["18.2", "text-box-trim", "trim-both"]
];

/** CSS.supports probes estimate the engine since the UA string is frozen/spoofable in webviews. */
export function estimateWebKitVersion(): string {
  if (typeof CSS === "undefined" || typeof CSS.supports !== "function") {
    return "unknown";
  }
  const match = WEBKIT_PROBES.find(([, property, value]) =>
    CSS.supports(property, value)
  );
  return match ? `webkit>=${match[0]}` : "unknown";
}

/**
 * Gets or generates a persistent device UUID for web platform
 * Stored in localStorage to persist across sessions
 */
function getWebDeviceId(): string {
  try {
    let uuid = localStorage.getItem(WEB_DEVICE_UUID_KEY);

    if (!uuid) {
      uuid = nanoid();
      localStorage.setItem(WEB_DEVICE_UUID_KEY, uuid);
    }

    return uuid;
  } catch (error) {
    // If localStorage is unavailable (e.g., private browsing), generate temporary ID
    reportErrorSentry(error);
    return nanoid();
  }
}
